import React, { useState, useEffect, useRef } from "react";
import { Modal, DatePicker, Space, message, Button, Typography } from "antd";
import { useIntl } from "react-intl";
import { getBranchHouseVisitsReport } from "../../../api/server";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "dayjs/locale/he";
import { CloseOutlined } from "@ant-design/icons";
import Draggable from "react-draggable";
import { getPdfMargins } from "../../../components/util/helpers";
import { exportReportToPdf } from "../../../components/util/PDFExporter";
import { useUserBranches } from "../../../hooks/accessControl";
import { CSVLink } from "react-csv";

// Extend dayjs with the plugin
dayjs.extend(isSameOrBefore);

const { Title } = Typography;
const { RangePicker } = DatePicker;

const styles = {
  dragHandle: {
    width: "100%",
    cursor: "move",
    height: "50px",
    position: "absolute",
    left: "0",
    top: "0",
  },
  closeButton: {
    position: "absolute",
    left: 10,
    top: 10,
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px 40px 40px 40px",
  },
  title: {
    fontSize: "30px",
    fontWeight: 500,
    lineHeight: 1.27,
    color: "rgba(0, 0, 0, 0.85)",
    margin: "13px 0 40px 0",
    textAlign: "center",
  },
  inputWrapper: {
    width: "300px",
  },
  datePicker: {
    width: "100%",
  },
  submitButton: {
    flexGrow: 1,
  },
  buttonWrapper: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    gap: "10px",
  },
};

const getYearMonth = (date) => date.year() * 12 + date.month();

// Disabled 14 months from the selected date
const disabled14MonthsDate = (current, { from, type }) => {
  if (from) {
    const minDate = from.add(-14, "months");
    const maxDate = from.add(14, "months");
    switch (type) {
      case "year":
        return (
          current.year() < minDate.year() || current.year() > maxDate.year()
        );
      default:
        return (
          getYearMonth(current) < getYearMonth(minDate) ||
          getYearMonth(current) > getYearMonth(maxDate)
        );
    }
  }
  return false;
};

const HouseVisitsReportModal = ({ visible, onCancel, branchId }) => {
  const [dateRange, setDateRange] = useState(null);
  const [disabled, setDisabled] = useState(true);
  const [branchName, setBranchName] = useState("");
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const intl = useIntl();
  const userBranches = useUserBranches();
  const csvLinkRef = useRef();

  useEffect(() => {
    setDateRange([dayjs().subtract(1, "year"), dayjs()]);
    setCsvData([]);
    setCsvHeaders([]);
    setIsDownloading(false);
  }, [visible]);

  useEffect(() => {
    const branch = userBranches.find((i) => i.id === +branchId);
    if (branch) {
      setBranchName(branch.name);
    }
  }, [userBranches, branchId]);

  useEffect(() => {
    setCsvData([]);
    setCsvHeaders([]);
    setIsDownloading(false);
  }, [dateRange]);

  const handleGenerateReport = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error(intl.formatMessage({ id: "app.patients.selectDateRange" }));
      return null;
    }

    try {
      const report = await getBranchHouseVisitsReport(branchId, {
        query: {
          filter: {
            $and: [
              {
                date: {
                  $gte: dateRange[0].startOf("month").format(),
                },
              },
              {
                date: {
                  $lte: dateRange[1].endOf("month").format(),
                },
              },
              { approved: true },
            ],
          },
        },
      });

      const months = getMonthRange(dateRange[0], dateRange[1]);
      const processedData = processReportData(report?.data?.items, months);

      const headers = [
        {
          label: intl.formatMessage({ id: "app.patients.fullname" }),
          key: "שם המטופל",
        },
        ...months.map((month) => ({ label: month, key: month })),
      ];
      setCsvHeaders(headers);
      setCsvData(processedData);

      return { processedData, months };
    } catch (error) {
      console.error("Error fetching report:", error);
      message.error(
        intl.formatMessage({ id: "app.patients.reportFetchError" })
      );
      setCsvData([]);
      setCsvHeaders([]);
      return null;
    }
  };

  const handlePrint = async () => {
    const reportData = await handleGenerateReport();
    if (!reportData) return;

    const { processedData, months } = reportData;

    const items = getItems(processedData);

    const pageWidth = 297;
    const margins = 10;
    const availableWidth = pageWidth - 2 * margins;
    const nameColumnWidth = 30;
    const remainingWidth = availableWidth - nameColumnWidth;
    const monthColumnWidth = Math.floor(remainingWidth / months.length);

    exportReportToPdf(
      intl.formatMessage({ id: "app.patients.houseVisitsReport" }),
      items,
      {
        setHeader,
        headerRecord: null,
        intl,
        category: "house_visits_report",
        tableOptions: {
          styles: { fontSize: 9 },
          columnStyles: {
            "שם המטופל": { cellWidth: nameColumnWidth },
            ...months.reduce((acc, month) => {
              acc[month] = { cellWidth: monthColumnWidth };
              return acc;
            }, {}),
          },
          margin: { top: 36, left: margins, right: margins },
        },
        orientation: "l",
        action: "print",
      }
    );
  };

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    const reportData = await handleGenerateReport();

    if (reportData && csvLinkRef.current) {
      setTimeout(() => {
        csvLinkRef.current.link.click();
        setIsDownloading(false);
      }, 0);
    } else {
      setIsDownloading(false);
    }
  };

  const getMonthRange = (startDate, endDate) => {
    const months = [];
    let currentDate = dayjs(startDate).locale("he");
    const lastDate = dayjs(endDate).locale("he");

    while (currentDate.isSameOrBefore(lastDate, "month")) {
      months.push(currentDate.format("MM/YY"));
      currentDate = currentDate.add(1, "month");
    }

    return months;
  };

  const processReportData = (items, months) => {
    if (!items || items.length === 0) {
      console.warn("No items to process");
      return [];
    }

    const groupedData = items.reduce((acc, item) => {
      const patientId = item.patient_id;
      if (!acc[patientId]) {
        acc[patientId] = {
          id: patientId,
          "שם המטופל": `${item.patient_surename} ${item.patient_first_name}`,
        };
        months.forEach((month) => {
          acc[patientId][month] = "";
        });
      }

      const date = dayjs(item.date).locale("he");
      const care_giver_was_presented = item.care_giver_was_presented
        ? " +"
        : " -";
      const role = item.planned_employee_controller
        ? " ב"
        : item.planned_employee_professional_role_id
        ? " מ"
        : " ";
      const monthKey = date.format("MM/YY");
      if (months.includes(monthKey)) {
        acc[patientId][monthKey] = `${date.format(
          "DD/MM"
        )}${role}${care_giver_was_presented}`;
      }

      return acc;
    }, {});

    const sortedGroupedData = Object.values(groupedData).sort((a, b) =>
      a["שם המטופל"].localeCompare(b["שם המטופל"], "he")
    );

    return sortedGroupedData;
  };

  const getItems = (data) => {
    return data.map(({ id, ...rest }) => {
      const reversedItem = {};
      Object.keys(rest)
        .reverse()
        .forEach((key) => {
          reversedItem[key] = rest[key];
        });
      return reversedItem;
    });
  };

  const setHeader = (doc, record) => {
    doc.setFontSize(16);
    doc.setTextColor(40);
    doc.setR2L(true);
    doc.setFont("Rubik", "normal");
    const title = intl.formatMessage({ id: "app.patients.houseVisitsReport" });
    const margins = getPdfMargins(doc);
    const hLoc = doc.internal.pageSize.getWidth() - 10;
    const report = "תאריך הדפסה";
    const date = `${dayjs().format("HH:mm DD/MM/YYYY")}`;
    const reportDate = `${report} - ${date}`;
    var subTitle = branchName;
    const legend = `"מ"=עובד מקצועי, "ב"=בקר, "+"=עם מטפלת, "-"=בלי מטפלת`;
    doc.text(subTitle, hLoc, 26, {
      align: "right",
    });
    doc.text(title, hLoc, 20, {
      align: "right",
    });
    doc.setFontSize(12);
    doc.text(reportDate, margins, 20, {
      align: "left",
    });
    doc.setFontSize(10);
    doc.text(legend, hLoc, 34, {
      align: "right",
    });
  };

  return (
    <Modal
      closable={false}
      footer={null}
      maskClosable={false}
      title={null}
      width={"600px"}
      styles={{
        body: {
          padding: 0,
        },
      }}
      open={visible}
      modalRender={(modal) => (
        <Draggable disabled={disabled}>{modal}</Draggable>
      )}
    >
      <div>
        <div
          style={styles.dragHandle}
          onMouseOver={() => setDisabled(false)}
          onMouseOut={() => setDisabled(true)}
        >
          <Button
            style={styles.closeButton}
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onCancel}
          />
        </div>
        <div style={styles.contentWrapper}>
          <Title style={styles.title}>
            {intl.formatMessage({ id: "app.patients.houseVisitsReport" })}
          </Title>
          <Space direction="vertical" size={20} style={styles.inputWrapper}>
            <RangePicker
              style={styles.datePicker}
              onChange={(dates) => setDateRange(dates)}
              format="MM/YYYY"
              defaultValue={[dayjs().subtract(1, "year"), dayjs()]}
              disabledDate={disabled14MonthsDate}
              picker="month"
            />

            <div style={styles.buttonWrapper}>
              <Button
                type="primary"
                onClick={handlePrint}
                disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                style={styles.submitButton}
              >
                {intl.formatMessage({ id: "app.actions.print" })}
              </Button>

              <Button
                type="default"
                onClick={handleDownloadExcel}
                disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                loading={isDownloading}
                style={styles.submitButton}
              >
                {intl.formatMessage({ id: "app.actions.download" })}
              </Button>

              <CSVLink
                data={csvData}
                headers={csvHeaders}
                filename={`${intl.formatMessage({
                  id: "app.patients.houseVisitsReport",
                })}_${branchName}_${
                  dateRange ? dateRange[0].format("YYYYMM") : ""
                }-${dateRange ? dateRange[1].format("YYYYMM") : ""}.csv`}
                target="_blank"
                ref={csvLinkRef}
                style={{ display: "none" }}
              />
            </div>
          </Space>
        </div>
      </div>
    </Modal>
  );
};

export default HouseVisitsReportModal;
